import { saveLanguage } from './client'
import { Capacitor } from '@capacitor/core'
import Icon from '../components/Icon'
import { getLocale, setLocale, type Locale } from '../lib/i18n'
import { tw } from './strings'
import { sv } from './servicesStrings'
import type { Side } from './SideSwitch'
import { usePageSurface } from './surface'

/** Full-viewport entry; browsing remains available without an account. */
export default function Welcome({
  onChoose,
  onSignIn,
  onRegister,
  signedIn = false,
}: {
  onChoose: (side: Side) => void
  onSignIn?: () => void
  /** Straight to account creation, with no door chosen first; the role is asked afterwards. */
  onRegister?: () => void
  /** Changes the third action from "sign in" to "back". */
  signedIn?: boolean
}) {
  usePageSurface('white')

  const locale = getLocale()
  const pick = (next: Locale) => {
    if (next === locale) return
    setLocale(next)
    void saveLanguage(next)
    // Same as core's LanguageToggle: the string helpers read the locale when
    // they are called, so re-rendering one component would leave the rest of
    // the tree in the old language.
    window.location.reload()
  }

  return (
    <section className="w-splash" aria-labelledby="splash-title">
      <div className="w-splash-lang" role="group" aria-label="Language / Idioma">
        <button aria-pressed={locale === 'en'} onClick={() => pick('en')} lang="en">EN</button>
        <span aria-hidden="true">|</span>
        <button aria-pressed={locale === 'es-PR'} onClick={() => pick('es-PR')} lang="es">ES</button>
      </div>

      <header className="w-splash-mark">
        <img src="/icon-192.png" alt="" width={64} height={64} />
        <div>
          <h1 id="splash-title" className="w-wordmark">Vieques Worx</h1>
          <p className="w-tagline">{tw('w.tagline')}</p>
        </div>
      </header>

      <div className="w-splash-art">
      <img
        className="w-splash-illo"
        src="/splash-corrected-1200.webp"
        srcSet="/splash-corrected-800.webp 800w, /splash-corrected-1200.webp 1200w"
        sizes="(min-width: 900px) 58vw, 100vw"
        width={1200}
        height={638}
        alt={tw('w.splash.illoAlt')}
        /* Eager and high priority: it is the largest thing on the first screen
           somebody ever sees, and lazy-loading it would animate the page
           apart under them. */
        loading="eager"
        fetchPriority="high"
        decoding="async"
      />
      </div>

      <div className="w-splash-actions">
        <div className="w-splash-paths">
          <button className="w-path w-path-worker" onClick={() => onChoose('worker')}>
            <Icon name="person" size={34} className="w-path-icon" />
            <span className="w-path-text">
              <span className="w-path-title">{tw('w.lookingForWork')}</span>
              <span className="w-path-sub">{tw('w.lookingForWorkSub')}</span>
            </span>
            <Icon name="chevron" size={24} className="w-path-go" />
          </button>

          <button className="w-path w-path-employer" onClick={() => onChoose('employer')}>
            <Icon name="briefcase" size={34} className="w-path-icon" />
            <span className="w-path-text">
              <span className="w-path-title">{tw('w.hiring')}</span>
              <span className="w-path-sub">{tw('w.hiringSub')}</span>
            </span>
            <Icon name="chevron" size={24} className="w-path-go" />
          </button>

          {/* The third door: not a job at all. Someone who does the work
              (plumber, electrician, hauling) lists it; a neighbour asks. */}
          <button className="w-path w-path-pro" onClick={() => onChoose('pro')}>
            <Icon name="wrench" size={34} className="w-path-icon" />
            <span className="w-path-text">
              <span className="w-path-title">{sv('sv.path.title')}</span>
              <span className="w-path-sub">{sv('sv.path.sub')}</span>
            </span>
            <Icon name="chevron" size={24} className="w-path-go" />
          </button>
        </div>

        {signedIn && <p className="w-splash-already muted">{tw('w.notLocked')}</p>}
        {onSignIn && (
          <div className="w-splash-signin">
            {/* Both, side by side: registering is for somebody new, signing in
                for somebody who already did. The doors above are for LOOKING
                AROUND first; neither of these needs one chosen. */}
            <div className="w-splash-cta">
              {onRegister && (
                <button className="w-register-btn" onClick={onRegister}>
                  <Icon name="plus" size={18} />
                  <span>{tw('w.register')}</span>
                </button>
              )}
              <button className="w-signin" onClick={onSignIn}>
                <span>{tw('w.signIn')}</span>
              </button>
            </div>
          </div>
        )}

        {/* Pointless inside the app that came from the store. */}
        {!Capacitor.isNativePlatform() && (
          <a
            className="w-splash-play"
            href="https://play.google.com/store/apps/details?id=com.viequesworx.app"
            target="_blank"
            rel="noopener noreferrer"
          >
            {/* Google's own badge, unaltered, per language. */}
            <img
              className={`w-splash-play-badge w-splash-play-badge-${locale === 'en' ? 'en' : 'es'}`}
              src={locale === 'en' ? '/google-play-badge-en.png' : '/google-play-badge-es.png'}
              alt={tw('w.splash.getOnPlay')}
              width={646}
              height={250}
            />
          </a>
        )}
      </div>
    </section>
  )
}
